import { getCsrfConfigForFetch } from "../utils/csrf-utils";
import { backendURLBackend, backendURLMeeting, backendURLSSO } from "./constants";


export async function fetchMeetingReport({ streamId, filters, page, size, sort }) {
  let url = `${backendURLMeeting}/api/v1/meetings/reports?streamId=${streamId}`;
  
  if (page !== undefined) url += `&page=${page}`;
  if (size !== undefined) url += `&size=${size}`;

  if (sort) {
    const sortArray = Array.isArray(sort) ? sort : [sort];
    
    sortArray.forEach(s => {
      url += `&sort=${s}`; 
    });
  }

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getCsrfConfigForFetch(),
    },
    credentials: "include",
    body: JSON.stringify({ filters: filters ?? [] }),
  });
}

export async function fetchMeetingReportExcel({ streamId, filters, sort }) {
  let url = `${backendURLMeeting}/api/v1/meetings/reports/excel?streamId=${streamId}`;

  if (sort) {
    if (Array.isArray(sort)) {
      sort.forEach(s => url += `&sort=${s}`);
    } else {
      url += `&sort=${sort}`;
    }
  }

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ...getCsrfConfigForFetch(),
    },
    credentials: "include",
    body: JSON.stringify({ filters: filters ?? [] }),
  });
}

function toSortArray(sort) {
  if (Array.isArray(sort)) return sort;
  if (sort) return [sort];
  return [];
}

export async function fetchMyTrackerMeetingReport({ filters, page, size, sort, streamId, includeInactiveStreams = false }) {
  let url = `${backendURLMeeting}/api/v1/tracker/meetings/reports/my?page=${page}&size=${size}`;
  if (streamId) url += `&streamId=${encodeURIComponent(streamId)}`;
  url += `&includeInactiveStreams=${includeInactiveStreams}`;
  const sortArray = toSortArray(sort);
  sortArray.forEach((sortParam) => {
    url += `&sort=${sortParam}`;
  });

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getCsrfConfigForFetch(),
    },
    credentials: "include",
    body: JSON.stringify({ filters: filters ?? [] }),
  });
}

export async function fetchMyTrackerMeetingReportExcel({ filters, sort, streamId, includeInactiveStreams = false }) {
  const params = new URLSearchParams();
  if (streamId) params.append("streamId", streamId);
  params.append("includeInactiveStreams", String(includeInactiveStreams));
  const sortArray = toSortArray(sort);
  sortArray.forEach((sortParam) => params.append("sort", sortParam));
  const query = params.toString();
  let url = `${backendURLMeeting}/api/v1/tracker/meetings/reports/my/excel`;
  if (query) url += `?${query}`;

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ...getCsrfConfigForFetch(),
    },
    credentials: "include",
    body: JSON.stringify({ filters: filters ?? [] }),
  });
}

export async function fetchReports({ page, size, filters }) {
  const response = await fetch(
    `${backendURLBackend}/api/v1/team-cards/reports?page=${page}&size=${size}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
      body: JSON.stringify({
        filters: filters ?? []
      }),
    }
  );

  return response;
}

export async function fetchReportExcel({ filters }) {
  const response = await fetch(
    `${backendURLBackend}/api/v1/team-cards/reports/excel`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
      body: JSON.stringify({ filters: filters ?? [] }),
    }
  );
  return response;
}

export async function fetchTrackers({ page, size, sort, filters }) {
  let sortString;
  if (Array.isArray(sort) && sort.length !== 0) {
    sortString = "sort=" + sort.join("&sort=")
  } else {
    sortString = "sort=username,asc";
  }
  const response = await fetch(
    `${backendURLSSO}/api/v1/users/trackers?page=${page}&size=${size}&${sortString}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
      body: JSON.stringify({
        filters: filters ?? [],
      }),
    }
  );

  return response;
}

export async function fetchStreams({ page, size, sort }) {
  let sortString;
  if (Array.isArray(sort) && sort.length !== 0) {
    sortString = "sort=" + sort.join("&sort=")
  } else {
    sortString = "sort=name,asc";
  }
  const response = await fetch(
    `${backendURLBackend}/api/v1/streams?page=${page}&size=${size}&${sortString}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
      body: JSON.stringify({
        filters: []
        // TODO: add filters to request
        // filters: selectedFilters 
      }),
    }
  );

  return response;
}

export async function fetchTeams({ page, size, filters, admin = false }) {
  const response = await fetch(
    `${backendURLBackend}/api/v1/${admin ? "admin/" : ""}team-cards?page=${page}&size=${size}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
      body: JSON.stringify({
        filters: filters ?? []
      }),
    }
  );

  return response;
}

export async function fetchUserInfo({ username = null }) {
  const endpoint = username === null ?
    `${backendURLSSO}/api/v1/account/info` :
    `${backendURLSSO}/api/v1/users/${username}/info`;
  const response = await fetch(
    endpoint,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
    }
  );

  return response;
}

export async function fetchUserPhoto({ username = null }) {
  const endpoint = username === null ?
    `${backendURLSSO}/api/v1/account/photo` :
    `${backendURLSSO}/api/v1/users/${username}/photo`;
  const response = await fetch(
    endpoint,
    {
      method: "GET",
      headers: {
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
    }
  );

  return response;
}

export async function updateUserInfo({ newUserData }) {
  const response = await fetch(
    `${backendURLSSO}/api/v1/account/update`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
      body: JSON.stringify(newUserData),
    }
  );

  return response;
}

export async function updateUserPhoto({ newUserPhotoFile }) {
  const formData = new FormData();
  formData.append("file", newUserPhotoFile);
  const response = await fetch(
    `${backendURLSSO}/api/v1/account/photo`,
    {
      method: "POST",
      headers: {
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
      body: formData,
    }
  );

  return response;
}

export async function fetchUserTeams(username) {
  const response = await fetch(
    `${backendURLBackend}/api/v1/admin/team-cards/by-user?username=${encodeURIComponent(username)}`,
    {
      method: "GET",
      headers: {
        "Accept": "application/json",
        ...getCsrfConfigForFetch(),
      },
      credentials: "include",
    }
  );
  return response;
}
